// Optimistic Updates Hook
// Provides instant UI feedback with automatic rollback on failure

import { useCallback } from 'react';
import { toast } from 'sonner';
import { useDoctors, useBookings, useClinics } from '../context/AppContext';
import { doctorService, bookingService, clinicService } from '../services';

// Generate temporary ID for optimistic updates
const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const useOptimisticDoctors = () => {
  const { doctors, loading, addDoctor, updateDoctor, deleteDoctor, setDoctors, setLoading } = useDoctors();

  const createDoctorOptimistic = useCallback(async (doctorData) => {
    // Create optimistic doctor with temp ID
    const optimisticDoctor = {
      ...doctorData,
      _id: generateTempId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      __optimistic: true,
    };

    // Immediately add to UI
    addDoctor(optimisticDoctor);
    toast.success("Doctor created!");

    try {
      // Make actual API call
      const realDoctor = await doctorService.createDoctor(doctorData);
      
      // Replace optimistic doctor with real one
      updateDoctor({ ...realDoctor, __replaces: optimisticDoctor._id });
      
      return realDoctor;
    } catch (error) {
      // Remove optimistic doctor on failure
      deleteDoctor(optimisticDoctor._id);
      toast.error("Failed to create doctor. Please try again.");
      throw error;
    }
  }, [addDoctor, updateDoctor, deleteDoctor]);

  const updateDoctorOptimistic = useCallback(async (doctorId, doctorData) => {
    // Find existing doctor
    const existingDoctor = doctors.find(d => d._id === doctorId);
    if (!existingDoctor) {
      throw new Error('Doctor not found');
    }

    // Create optimistic update
    const optimisticDoctor = {
      ...existingDoctor,
      ...doctorData,
      updatedAt: new Date().toISOString(),
      __optimistic: true,
    };

    // Immediately update UI
    updateDoctor(optimisticDoctor);
    toast.success("Doctor updated!");

    try {
      // Make actual API call
      const realDoctor = await doctorService.updateDoctor(doctorId, doctorData);
      
      // Replace with real data
      updateDoctor(realDoctor);
      
      return realDoctor;
    } catch (error) {
      // Rollback to original doctor on failure
      updateDoctor(existingDoctor);
      toast.error("Failed to update doctor. Changes reverted.");
      throw error;
    }
  }, [doctors, updateDoctor]);

  const deleteDoctorOptimistic = useCallback(async (doctorId) => {
    // Find existing doctor for potential rollback
    const existingDoctor = doctors.find(d => d._id === doctorId);
    if (!existingDoctor) {
      throw new Error('Doctor not found');
    }

    // Immediately remove from UI
    deleteDoctor(doctorId);
    toast.success("Doctor deleted!");

    try {
      // Make actual API call
      await doctorService.deleteDoctor(doctorId);
    } catch (error) {
      // Restore doctor on failure
      addDoctor(existingDoctor);
      toast.error("Failed to delete doctor. Restored.");
      throw error;
    }
  }, [doctors, deleteDoctor, addDoctor]);

  const refreshDoctors = useCallback(async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      const response = await doctorService.getAllDoctors();
      setDoctors(response.data || response, response.pagination);
    } catch (error) {
      toast.error("Failed to refresh doctors");
      throw error;
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [setDoctors, setLoading]);

  return {
    doctors,
    loading,
    createDoctorOptimistic,
    updateDoctorOptimistic,
    deleteDoctorOptimistic,
    refreshDoctors,
  };
};

export const useOptimisticBookings = () => {
  const { bookings, addBooking, updateBooking, deleteBooking, setBookings } = useBookings();

  const createBookingOptimistic = useCallback(async (bookingData) => {
    const optimisticBooking = {
      ...bookingData,
      _id: generateTempId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      __optimistic: true,
    };

    addBooking(optimisticBooking);
    toast.success("Booking created!");

    try {
      const realBooking = await bookingService.createBooking(bookingData);
      updateBooking({ ...realBooking, __replaces: optimisticBooking._id });
      return realBooking;
    } catch (error) {
      deleteBooking(optimisticBooking._id);
      toast.error("Failed to create booking. Please try again.");
      throw error;
    }
  }, [addBooking, updateBooking, deleteBooking]);

  const updateBookingOptimistic = useCallback(async (bookingId, bookingData) => {
    const existingBooking = bookings.find(b => b._id === bookingId);
    if (!existingBooking) {
      throw new Error('Booking not found');
    }

    const optimisticBooking = {
      ...existingBooking,
      ...bookingData,
      updatedAt: new Date().toISOString(),
      __optimistic: true,
    };

    updateBooking(optimisticBooking);
    toast.success("Booking updated!");

    try {
      const realBooking = await bookingService.updateBooking(bookingId, bookingData);
      updateBooking(realBooking);
      return realBooking;
    } catch (error) {
      updateBooking(existingBooking);
      toast.error("Failed to update booking. Changes reverted.");
      throw error;
    }
  }, [bookings, updateBooking]);

  const deleteBookingOptimistic = useCallback(async (bookingId) => {
    const existingBooking = bookings.find(b => b._id === bookingId);
    if (!existingBooking) {
      throw new Error('Booking not found');
    }

    deleteBooking(bookingId);
    toast.success("Booking deleted!");

    try {
      await bookingService.deleteBooking(bookingId);
    } catch (error) {
      addBooking(existingBooking);
      toast.error("Failed to delete booking. Restored.");
      throw error;
    }
  }, [bookings, deleteBooking, addBooking]);

  const refreshBookings = useCallback(async () => {
    try {
      const response = await bookingService.getAllBookings();
      setBookings(response.data || response, response.pagination);
    } catch (error) {
      toast.error("Failed to refresh bookings");
      throw error;
    }
  }, [setBookings]);

  return {
    bookings,
    createBookingOptimistic,
    updateBookingOptimistic,
    deleteBookingOptimistic,
    refreshBookings,
  };
};

export const useOptimisticClinics = () => {
  const { clinics, addClinic, updateClinic, deleteClinic, setClinics } = useClinics();

  const createClinicOptimistic = useCallback(async (clinicData) => {
    const optimisticClinic = {
      ...clinicData,
      _id: generateTempId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      __optimistic: true,
    };

    addClinic(optimisticClinic);
    toast.success("Clinic created!");

    try {
      const realClinic = await clinicService.createClinic(clinicData);
      updateClinic({ ...realClinic, __replaces: optimisticClinic._id });
      return realClinic;
    } catch (error) {
      deleteClinic(optimisticClinic._id);
      toast.error("Failed to create clinic. Please try again.");
      throw error;
    }
  }, [addClinic, updateClinic, deleteClinic]);

  const updateClinicOptimistic = useCallback(async (clinicId, clinicData) => {
    const existingClinic = clinics.find(c => c._id === clinicId);
    if (!existingClinic) {
      throw new Error('Clinic not found');
    }

    const optimisticClinic = {
      ...existingClinic,
      ...clinicData,
      updatedAt: new Date().toISOString(),
      __optimistic: true,
    };

    updateClinic(optimisticClinic);
    toast.success("Clinic updated!");

    try {
      const realClinic = await clinicService.updateClinic(clinicId, clinicData);
      updateClinic(realClinic);
      return realClinic;
    } catch (error) {
      updateClinic(existingClinic);
      toast.error("Failed to update clinic. Changes reverted.");
      throw error;
    }
  }, [clinics, updateClinic]);

  const deleteClinicOptimistic = useCallback(async (clinicId) => {
    const existingClinic = clinics.find(c => c._id === clinicId);
    if (!existingClinic) {
      throw new Error('Clinic not found');
    }

    deleteClinic(clinicId);
    toast.success("Clinic deleted!");

    try {
      await clinicService.deleteClinic(clinicId);
    } catch (error) {
      addClinic(existingClinic);
      toast.error("Failed to delete clinic. Restored.");
      throw error;
    }
  }, [clinics, deleteClinic, addClinic]);

  const refreshClinics = useCallback(async () => {
    try {
      const response = await clinicService.getAllClinics();
      setClinics(response.data || response, response.pagination);
    } catch (error) {
      toast.error("Failed to refresh clinics");
      throw error;
    }
  }, [setClinics]);

  return {
    clinics,
    createClinicOptimistic,
    updateClinicOptimistic,
    deleteClinicOptimistic,
    refreshClinics,
  };
};

// Generic optimistic update hook
export const useOptimisticUpdate = (entity, service) => {
  const performOptimisticUpdate = useCallback(async (operation, data, id = null) => {
    try {
      switch (operation) {
        case 'create':
          return await service.create(data);
        case 'update':
          return await service.update(id, data);
        case 'delete':
          return await service.delete(id);
        default:
          throw new Error(`Unknown operation: ${operation}`);
      }
    } catch (error) {
      console.error(`Optimistic ${operation} failed for ${entity}:`, error);
      throw error;
    }
  }, [entity, service]);

  return { performOptimisticUpdate };
};
